import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Human-readable labels for parameters
const parameterLabels = {
  chamber1Temp: "Chamber 1 Temp",
  chamber2Temp: "Chamber 2 Temp",
  coolingTime: "Cooling Time",
  remarks: "Remarks",
  totalCycleTime: "Total Cycle Time",
  vacuumTimeB1: "Vacuum Time B1",
  vacuumTimeB2: "Vacuum Time B2",
};

// Ordered parameters for rows
const orderedParameters = [
  "chamber1Temp",
  "chamber2Temp",
  "vacuumTimeB1",
  "vacuumTimeB2",
  "totalCycleTime",
  "coolingTime",
  "remarks",
];

const LaminationMachineTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default to standard shifts
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Collect all unique lines across the entire dataset (with fallback)
  const allLines = useMemo(() => {
    const lines = new Set();
    data.forEach((item) => {
      Object.keys(item.form_data || {}).forEach((line) => {
        if (line) lines.add(line);
      });
    });
    const arr = [...lines].sort();
    return arr.length > 0 ? arr : ["Line 1"];
  }, [data]);

  // Collect all unique laminators across the entire dataset (with fallback)
  const allLaminators = useMemo(() => {
    const laminators = new Set();
    data.forEach((item) => {
      Object.values(item.form_data || {}).forEach((lineData) => {
        if (typeof lineData === "object" && lineData !== null) {
          Object.keys(lineData).forEach((laminator) => {
            if (laminator) laminators.add(laminator);
          });
        }
      });
    });
    const arr = [...laminators].sort();
    return arr.length > 0 ? arr : ["Laminator 1"];
  }, [data]);

  // Flat list of all leaf columns
  const flatColumns = useMemo(() => {
    const list = [];
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        allLines.forEach((line) => {
          allLaminators.forEach((laminator) => {
            list.push({
              date,
              shift,
              line,
              laminator,
              id: `${date}_${shift}_${line}_${laminator}`,
            });
          });
        });
      });
    });
    return list;
  }, [dates, effectiveShifts, allLines, allLaminators]);

  // Fast data map
  const tableDataMap = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;

      Object.entries(item.form_data || {}).forEach(([line, lineData]) => {
        if (!lineData || typeof lineData !== "object") return;
        Object.entries(lineData).forEach(([laminator, laminatorData]) => {
          if (!laminatorData || typeof laminatorData !== "object") return;
          const keyPrefix = `${date}_${shift}_${line}_${laminator}`;
          orderedParameters.forEach((param) => {
            if (laminatorData[param] !== undefined) {
              map[`${keyPrefix}_${param}`] = laminatorData[param];
            }
          });
        });
      });
    });
    return map;
  }, [data]);

  // Column width definitions
  const columnWidths = useMemo(() => {
    return {
      parameter: 200,
      value: 120,
    };
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Static parameter rows
  const tableRows = useMemo(() => {
    return orderedParameters.map((param) => ({
      paramKey: param,
      parameter: parameterLabels[param] || param,
    }));
  }, []);

  // Structural spans for 4-level headers
  const laminatorsPerLine = allLaminators.length;
  const laminatorsPerShift = allLines.length * laminatorsPerLine;
  const laminatorsPerDate = effectiveShifts.length * laminatorsPerShift;

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Parameter"];
    const row2 = [""];
    const row3 = [""];
    const row4 = [""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 3, c: 0 } });

    let colIdx = 1;

    dates.forEach((date) => {
      row1.push(date);
      for (let i = 1; i < laminatorsPerDate; i++) row1.push("");
      if (laminatorsPerDate > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + laminatorsPerDate - 1 } });
      }

      effectiveShifts.forEach((shift) => {
        row2.push(`Shift ${shift}`);
        for (let i = 1; i < laminatorsPerShift; i++) row2.push("");
        if (laminatorsPerShift > 1) {
          merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + laminatorsPerShift - 1 } });
        }

        allLines.forEach((line) => {
          row3.push(line);
          for (let i = 1; i < laminatorsPerLine; i++) row3.push("");
          if (laminatorsPerLine > 1) {
            merges.push({ s: { r: 2, c: colIdx }, e: { r: 2, c: colIdx + laminatorsPerLine - 1 } });
          }

          allLaminators.forEach((laminator) => {
            row4.push(laminator);
            colIdx += 1;
          });
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      flatColumns.forEach((col) => {
        const val = tableDataMap[`${col.id}_${row.paramKey}`];
        r.push(val !== undefined && val !== "" ? val : "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, row3, row4, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 25 },
      ...Array(flatColumns.length).fill({ wch: 15 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Lamination Machine");

    const fileName = generateExcelFileName("Lamination_Machine", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Lamination Machine Parameters Verification
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Date */}
            <TableRow>
              <StickyTableCell
                rowSpan={4}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {dates.map((date) => (
                <CustomTableCell
                  key={date}
                  colSpan={laminatorsPerDate}
                  columnWidth={columnWidths.value * laminatorsPerDate}
                >
                  {date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shift */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.map((shift) => (
                  <CustomTableCell
                    key={`${date}_${shift}`}
                    colSpan={laminatorsPerShift}
                    columnWidth={columnWidths.value * laminatorsPerShift}
                  >
                    {shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Line */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.flatMap((shift) =>
                  allLines.map((line) => (
                    <CustomTableCell
                      key={`${date}_${shift}_${line}`}
                      colSpan={laminatorsPerLine}
                      columnWidth={columnWidths.value * laminatorsPerLine}
                    >
                      {line}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>

            {/* Row 4: Laminator */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.flatMap((shift) =>
                  allLines.flatMap((line) =>
                    allLaminators.map((laminator) => (
                      <CustomTableCell
                        key={`${date}_${shift}_${line}_${laminator}`}
                        columnWidth={columnWidths.value}
                      >
                        {laminator}
                      </CustomTableCell>
                    ))
                  )
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={1}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>

                  {flatColumns.map((col) => {
                    const val = tableDataMap[`${col.id}_${row.paramKey}`];
                    return (
                      <BodyTableCell key={`${col.id}_${row.paramKey}`} columnWidth={columnWidths.value}>
                        {val !== undefined && val !== "" ? String(val) : "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default LaminationMachineTable;